import React, { useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Badge, Box } from "@mui/material";
import { 
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Message as MessageIcon,
  School as SchoolIcon,
  CreditCard as CreditCardIcon,
  Person as PersonIcon
} from "@mui/icons-material";
import config from "config";
import "./Layout.css";
import { isProfileCompleted } from "../../utils";

const Layout = React.memo(({ children }) => {
  const location = useLocation();
  const { isGuest } = useSelector((state) => state.main);
  const profileUser = useSelector((state) => state.profile?.user);
  const profileUserId = useSelector((state) => state.main?.userId);
  const unreadMessageCount = useSelector(
    (state) => {
      const messages = state.profile?.messages;
      if (!messages || !Array.isArray(messages)) return 0;
      const count = messages.filter((message) => !message.isRead).length || 0;
      return count;
    },
    // Use shallow equality to ensure re-render when count changes
    (left, right) => left === right
  );

  // Determine active menu item based on current route
  const getActiveMenuItem = useCallback(() => {
    const pathname = location.pathname;
    
    if (pathname === "/dashboard" || pathname.startsWith("/dashboard")) {
      return "dashboard";
    } else if (pathname === "/subscriptions" || pathname.startsWith("/subscriptions")) {
      return "subscriptions";
    } else if (pathname === "/my-courses" || pathname.startsWith("/my-courses") || 
               pathname === "/course/new" || pathname.startsWith("/course/new") ||
               pathname.startsWith("/course/edit/") || pathname.startsWith("/course/renew/")) {
      return "my-courses";
    } else if (pathname === "/messages" || pathname.startsWith("/messages")) {
      return "messages";
    } else if (pathname === "/account-settings" || pathname.startsWith("/account-settings")) {
      return "account-settings";
    } else if (pathname === "/profile/edit" || pathname.startsWith("/profile/edit")) {
      return "profile-edit";
    }
    
    return "dashboard"; // Default fallback
  }, [location.pathname]);

  const activeMenuItem = getActiveMenuItem();

  return (
    <div className="dashboard-container">
      <div className="dashboard-menu">
        
        {/* Dashboard */}
        {isGuest || (
          <Link
            className={
              "tab" +
              (activeMenuItem === "dashboard" ? " active " + config["class-name"] : "")
            }
            to="/dashboard"
          >
                 <DashboardIcon sx={{ fontSize: 20, mr: 1.5, color: 'inherit' }} />
            Dashboard
          </Link>
        )}

        {/* Profile Edit (Guest only) - appears above Account Settings */}
        {isGuest && (
          <Link
            className={
              "tab" +
              (activeMenuItem === "profile-edit" ? " active " + config["class-name"] : "")
            }
            to="/profile/edit"
          >
            <PersonIcon sx={{ fontSize: 20, mr: 1.5, color: 'inherit' }} />
            Profile
          </Link>
        )}

        {/* Account Settings */}
        <Link
          className={
            "tab" +
            (activeMenuItem === "account-settings" ? " active " + config["class-name"] : "")
          }
          to="/account-settings"
        >
                 <SettingsIcon sx={{ fontSize: 20, mr: 1.5, color: 'inherit' }} />
          Account Settings
        </Link>

        {/* Messages */}
        <Link
          className={
            "tab" +
            (activeMenuItem === "messages" ? " active " + config["class-name"] : "")
          }
          to="/messages"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
            <MessageIcon sx={{ fontSize: 20, mr: 1.5, color: 'inherit', flexShrink: 0 }} />
            <Box component="span" sx={{ flex: 1, minWidth: 0 }}>Messages</Box>
          </Box>
          {unreadMessageCount > 0 && (
            <Badge 
              badgeContent={unreadMessageCount} 
              color="error"
              sx={{ 
                ml: 1,
                mr: 1,
                flexShrink: 0,
                '& .MuiBadge-badge': {
                  position: 'static',
                  transform: 'none',
                }
              }} 
            />
          )}
        </Link>

        {/* My Courses (tutor only) */}
        {!isGuest && (
          <Link
            className={
              "tab" +
              (activeMenuItem === "my-courses" ? " active " + config["class-name"] : "")
            }
            to="/my-courses"
          >
            <SchoolIcon sx={{ fontSize: 20, mr: 1.5, color: 'inherit' }} />
            My Courses
          </Link>
        )}

        {/* Subscriptions */}
        {profileUserId && isProfileCompleted(profileUser) && (
          <Link
            className={
              "tab" +
              (activeMenuItem === "subscriptions" ? " active " + config["class-name"] : "")
            }
            to="/subscriptions"
          >
                   <CreditCardIcon sx={{ fontSize: 20, mr: 1.5, color: 'inherit' }} />
            Payment Plans
          </Link>
        )}
      </div>

      <div className="dashboard-content">
        {children}
      </div>
    </div>
  );
});

Layout.displayName = 'Layout';

export default Layout;
